import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaSync,
  FaSpinner,
  FaTable,
  FaChartLine,
  FaChartArea
} from 'react-icons/fa';
import { TrainedModelsSidebarProps, TrainedModelWithMetrics } from './types';
import { useTrainedModels } from './hooks';
import { ModelListItem } from './components/ModelListItem';
import { EvaluationTablesModal } from './components/EvaluationTablesModal';
import { PlottingModal } from './components/PlottingModal';
import { ViolinPlotsModal } from './components/ViolinPlotsModal';
import { ModelCompatibilityBadge } from '../ModelCompatibilityBadge';

/**
 * Sidebar component for displaying trained models in a session
 * Supports model selection, download, and comparison
 */
export const TrainedModelsSidebar: React.FC<TrainedModelsSidebarProps> = memo(({
  sessionId,
  onModelSelect
}) => {
  const { t, i18n } = useTranslation('training');

  const {
    models,
    loading,
    error,
    selectedModel,
    refreshModels,
    selectModel,
    downloadModel,
    isDownloading
  } = useTrainedModels(sessionId);

  // State for evaluation tables modal
  const [showEvaluationModal, setShowEvaluationModal] = useState(false);

  // State for plotting modal
  const [showPlottingModal, setShowPlottingModal] = useState(false);

  // State for violin plots modal
  const [showViolinPlotsModal, setShowViolinPlotsModal] = useState(false);

  // Format date based on current language
  const formatDate = useCallback((dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  }, [i18n.language]);

  // Handle model selection
  const handleModelSelect = useCallback((model: TrainedModelWithMetrics) => {
    selectModel(model.filename);
    onModelSelect?.(model);
  }, [selectModel, onModelSelect]);

  // Handle download
  const handleDownload = useCallback((filename: string) => {
    downloadModel(filename);
  }, [downloadModel]);

  const hasModels = models.length > 0;

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border flex flex-col h-full overflow-visible">
      {/* Header */}
      <div className="px-5 py-4 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-foreground tracking-tight">
              {t('training.sidebar.title', 'Trained Models')}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t('training.sidebar.modelCount', '{{count}} model(s)', { count: models.length })}
            </p>
          </div>
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={() => setShowPlottingModal(true)}
              disabled={!hasModels}
              className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-150 ${
                hasModels ? 'text-muted-foreground hover:text-primary hover:bg-accent' : 'text-muted-foreground/50 cursor-not-allowed'
              }`}
              title={hasModels ? t('training.sidebar.generatePlots', 'Generate Plots') : t('training.sidebar.noModelsToPlot', 'Train a model first')}
            >
              <FaChartLine size={11} />
            </button>
            <button
              type="button"
              onClick={() => setShowViolinPlotsModal(true)}
              disabled={!hasModels}
              className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-150 ${
                hasModels ? 'text-muted-foreground hover:text-primary hover:bg-accent' : 'text-muted-foreground/50 cursor-not-allowed'
              }`}
              title={hasModels ? t('training.sidebar.viewViolinPlots', 'View Violin Plots') : t('training.sidebar.noModelsForViolin', 'Train a model first')}
            >
              <FaChartArea size={11} />
            </button>
            <button
              type="button"
              onClick={() => setShowEvaluationModal(true)}
              disabled={!hasModels}
              className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-150 ${
                hasModels ? 'text-muted-foreground hover:text-emerald-600 hover:bg-emerald-50' : 'text-muted-foreground/50 cursor-not-allowed'
              }`}
              title={hasModels ? t('training.sidebar.viewEvaluation', 'View Evaluation Tables') : t('training.sidebar.noModelsForEval', 'Train a model first')}
            >
              <FaTable size={11} />
            </button>
            <div className="w-px h-4 bg-border mx-1" />
            <button
              type="button"
              onClick={refreshModels}
              disabled={loading}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-accent disabled:text-muted-foreground/50 disabled:cursor-not-allowed transition-all duration-150"
              title={t('training.sidebar.refresh', 'Refresh')}
            >
              {loading ? (
                <FaSpinner className="animate-spin" size={11} />
              ) : (
                <FaSync size={11} />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Model compatibility info */}
      <div className="px-2 pb-2 border-b">
        <ModelCompatibilityBadge />
      </div>

      {/* Model list */}
      <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
        {loading && models.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mb-3">
              <FaSpinner className="animate-spin text-muted-foreground" />
            </div>
            <span className="text-sm">{t('training.sidebar.loading', 'Loading models...')}</span>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200/80 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-red-500 text-[10px] font-bold">!</span>
              </div>
              <p className="text-sm text-red-600 leading-relaxed">{error}</p>
            </div>
          </div>
        ) : models.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
              <svg className="w-5 h-5 text-muted-foreground/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
              </svg>
            </div>
            <p className="text-sm">{t('training.sidebar.empty', 'No models in this session')}</p>
          </div>
        ) : (
          <div role="list" className="space-y-1.5">
            {models.map(model => (
              <div role="listitem" key={model.filename}>
                <ModelListItem
                  model={model}
                  isSelected={selectedModel?.filename === model.filename}
                  isDownloading={isDownloading === model.filename}
                  onSelect={handleModelSelect}
                  onDownload={handleDownload}
                  formatDate={formatDate}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Evaluation Tables Modal */}
      <EvaluationTablesModal
        isOpen={showEvaluationModal}
        onClose={() => setShowEvaluationModal(false)}
        sessionId={sessionId}
      />

      {/* Plotting Modal */}
      <PlottingModal
        isOpen={showPlottingModal}
        onClose={() => setShowPlottingModal(false)}
        sessionId={sessionId}
      />

      {/* Violin Plots Modal */}
      <ViolinPlotsModal
        isOpen={showViolinPlotsModal}
        onClose={() => setShowViolinPlotsModal(false)}
        sessionId={sessionId}
      />
    </div>
  );
});

TrainedModelsSidebar.displayName = 'TrainedModelsSidebar';

export default TrainedModelsSidebar;
